import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router";
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Icon,
  Modal,
  StatTile,
  StatTileSkeleton,
  PageBody,
  SkeletonText,
  Textarea,
  useToast,
} from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./daily-report.css";

/**
 * Today's structured check-in, and a short history of past ones.
 *
 * A manager's view of the *team* is a separate page (DailyReportTeam) —
 * this one is always "your own", whether you manage anyone or not.
 *
 * Submitting does not drop you back into the same form pre-filled with what
 * you just typed — that read as "did this actually save?" with nothing to
 * look at afterward. Once today's report exists, this page shows it as a
 * read-only recap (the same shape a manager sees on a person's page) with
 * an explicit Edit action, and the rest of your history below it as a real
 * table instead of a stacked list.
 */

interface Report {
  id: string;
  date: string;
  whatIDid: string;
  blockers: string | null;
  tomorrowsPlan: string | null;
  updatedAt: string;
}

function formatDate(date: string): string {
  // `date` is a plain YYYY-MM-DD, not a timestamp — parsed as local, not UTC
  // midnight, so it doesn't drift a day back for anyone west of Greenwich.
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year!, month! - 1, day!).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function formatShortDate(date: string): string {
  const [year, month, day] = date.split("-").map(Number);
  const parsed = new Date(year!, month! - 1, day!);
  const sameYear = parsed.getFullYear() === new Date().getFullYear();
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
}

// The server computes each report's `date` from its author's own timezone
// at submit time; comparing against the browser's local calendar date is the
// same "today" as far as the person looking at this screen is concerned, so
// no round trip is needed just to ask what day the server thinks it is.
function todayKey(): string {
  return new Date().toLocaleDateString("en-CA");
}

function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/** `date` shifted back one workday, skipping whichever weekend falls in
 *  between — mirrors the API's own `addWorkday` in daily-reports.ts, since
 *  the two must agree on what a streak means for the same person. */
function previousWorkday(date: Date): Date {
  const cursor = new Date(date);
  do {
    cursor.setDate(cursor.getDate() - 1);
  } while (isWeekend(cursor));
  return cursor;
}

/**
 * How many workdays in a row, ending today or the last workday, have a
 * report. Weekends are skipped rather than counted as gaps — a report
 * filed Friday and again Monday is one continuous streak, matching the
 * server's own streak on the team rollup, since a manager and the person
 * themself must never see a different number for the same thing.
 *
 * Ending at the last workday still counts as "current" — someone reading
 * this before they have filed today's report should not see their streak
 * reset to zero just because the day is not over yet.
 */
function currentStreak(dates: Set<string>): number {
  let streak = 0;
  let cursor = new Date();
  while (isWeekend(cursor)) cursor.setDate(cursor.getDate() - 1);
  if (!dates.has(cursor.toLocaleDateString("en-CA"))) cursor = previousWorkday(cursor);

  while (dates.has(cursor.toLocaleDateString("en-CA"))) {
    streak += 1;
    cursor = previousWorkday(cursor);
  }
  return streak;
}

export function DailyReport() {
  usePageTitle("Daily report");
  const { toast } = useToast();
  const [reports, setReports] = useState<Report[] | null>(null);
  const [managesAnyone, setManagesAnyone] = useState(false);
  // Only meaningful once today's report exists — otherwise the form is the
  // only thing there is to show, editing is not a separate mode.
  const [editing, setEditing] = useState(false);

  const [whatIDid, setWhatIDid] = useState("");
  const [blockers, setBlockers] = useState("");
  const [tomorrowsPlan, setTomorrowsPlan] = useState("");
  const [saving, setSaving] = useState(false);
  // Table cells clamp long text to a couple of lines so every row stays the
  // same height regardless of how much was written that day — the full
  // report is one click away instead.
  const [openReport, setOpenReport] = useState<Report | null>(null);

  const load = useCallback(() => {
    Promise.all([
      api.get<{ reports: Report[] }>("/me/daily-reports"),
      api.get<{ team: unknown[] }>("/daily-reports/team").catch(() => ({ team: [] })),
    ])
      .then(([reportsRes, teamRes]) => {
        setReports(reportsRes.reports);
        setManagesAnyone(teamRes.team.length > 0);

        const todaysReport = reportsRes.reports.find((r) => r.date === todayKey());
        setWhatIDid(todaysReport?.whatIDid ?? "");
        setBlockers(todaysReport?.blockers ?? "");
        setTomorrowsPlan(todaysReport?.tomorrowsPlan ?? "");
      })
      .catch(() => toast("Could not load your reports.", { tone: "danger" }));
  }, [toast]);

  useEffect(() => load(), [load]);

  const today = reports?.find((r) => r.date === todayKey()) ?? null;
  // The form is the only thing to show until a report exists for today;
  // once it does, it's a read-only recap unless the person asks to edit it.
  const showForm = editing || (reports !== null && !today);

  // The form always edits whatever "today" currently holds — entering edit
  // mode re-seeds the fields from it, so cancelling never leaves behind a
  // half-typed edit the next time the form opens.
  const startEditing = useCallback(() => {
    setWhatIDid(today?.whatIDid ?? "");
    setBlockers(today?.blockers ?? "");
    setTomorrowsPlan(today?.tomorrowsPlan ?? "");
    setEditing(true);
  }, [today]);

  const submit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      if (!whatIDid.trim() || saving) return;

      setSaving(true);
      api
        .post<{ report: Report }>("/me/daily-reports", {
          whatIDid: whatIDid.trim(),
          blockers: blockers.trim() || undefined,
          tomorrowsPlan: tomorrowsPlan.trim() || undefined,
        })
        .then((res) => {
          setReports((prev) => [res.report, ...(prev ?? []).filter((r) => r.date !== res.report.date)]);
          setEditing(false);
          toast("Report saved.");
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not save your report.", { tone: "danger" });
        })
        .finally(() => setSaving(false));
    },
    [whatIDid, blockers, tomorrowsPlan, saving, toast],
  );

  const loading = reports === null;
  const past = useMemo(() => (reports ?? []).filter((r) => r.date !== todayKey()), [reports]);
  const streak = reports ? currentStreak(new Set(reports.map((r) => r.date))) : 0;
  const openBlockers = reports?.filter((r) => r.blockers).length ?? 0;

  return (
    <PageBody
      title="Daily report"
      subtitle="What you did, what's blocking you, what's next."
      width="regular"
      actions={managesAnyone ? <Link to="/daily-report/team">View team →</Link> : undefined}
    >
      {loading ? (
        <>
          <div className="daily-report__stats">
            <StatTileSkeleton />
            <StatTileSkeleton />
          </div>
          <SkeletonText lines={6} />
        </>
      ) : (
        <>
          <div className="daily-report__stats">
            <StatTile icon="repeat" value={streak} label="Day streak" tone={streak === 0 ? "warn" : "default"} />
            <StatTile
              icon="warning"
              value={openBlockers}
              label="Reports with blockers"
              tone={openBlockers > 0 ? "danger" : "default"}
            />
          </div>

          {showForm ? (
            <Card as="form" onSubmit={submit} className="daily-report__form">
              <Textarea
                label="What did you do today?"
                value={whatIDid}
                onChange={(e) => setWhatIDid(e.target.value)}
                rows={4}
                required
                autoFocus
              />
              <Textarea
                label="Anything blocking you?"
                value={blockers}
                onChange={(e) => setBlockers(e.target.value)}
                rows={4}
              />
              <Textarea
                label="What's the plan for tomorrow?"
                value={tomorrowsPlan}
                onChange={(e) => setTomorrowsPlan(e.target.value)}
                rows={4}
              />
              <div className="daily-report__actions">
                {today && (
                  <Button type="button" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
                    Cancel
                  </Button>
                )}
                <Button type="submit" loading={saving} disabled={!whatIDid.trim()}>
                  {today ? "Save" : "Submit"}
                </Button>
              </div>
            </Card>
          ) : (
            today && (
              <Card className="daily-report__summary">
                <div className="daily-report__summary-header">
                  <span className="daily-report__summary-date">Today · {formatDate(today.date)}</span>
                  <Button variant="secondary" size="sm" icon={<Icon name="edit" size={14} />} onClick={startEditing}>
                    Edit
                  </Button>
                </div>
                <p className="daily-report__summary-body">{today.whatIDid}</p>
                {today.blockers && (
                  <p className="daily-report__summary-blocker">
                    <Icon name="warning" size={13} /> Blocked: {today.blockers}
                  </p>
                )}
                {today.tomorrowsPlan && (
                  <p className="daily-report__history-label">
                    Next: <span>{today.tomorrowsPlan}</span>
                  </p>
                )}
              </Card>
            )
          )}

          {past.length > 0 && (
            <div className="daily-report__history">
              <h2>Past reports</h2>
              <DataTable
                caption="Your daily reports"
                density="compact"
                rows={past}
                rowKey={(report) => report.id}
                onRowClick={setOpenReport}
                empty={<EmptyState icon="clock" title="No past reports" />}
                columns={[
                  {
                    key: "date",
                    header: "Date",
                    width: "120px",
                    rowHeader: true,
                    render: (report) => <span className="daily-report-person__date">{formatShortDate(report.date)}</span>,
                  },
                  {
                    key: "whatIDid",
                    header: "What you did",
                    render: (report) => <span className="daily-report-person__cell">{report.whatIDid}</span>,
                  },
                  {
                    key: "blockers",
                    header: "Blockers",
                    width: "220px",
                    render: (report) =>
                      report.blockers ? (
                        <span className="daily-report-person__cell daily-report__summary-blocker">
                          {report.blockers}
                        </span>
                      ) : (
                        <span className="daily-report-team__muted">—</span>
                      ),
                  },
                  {
                    key: "tomorrowsPlan",
                    header: "Next",
                    render: (report) =>
                      report.tomorrowsPlan ? (
                        <span className="daily-report-person__cell">{report.tomorrowsPlan}</span>
                      ) : (
                        <span className="daily-report-team__muted">—</span>
                      ),
                  },
                ]}
              />
            </div>
          )}
        </>
      )}

      <Modal open={openReport !== null} onClose={() => setOpenReport(null)} title={openReport ? formatDate(openReport.date) : undefined}>
        {openReport && (
          <div className="daily-report-person__detail">
            <p className="daily-report-person__detail-body">{openReport.whatIDid}</p>
            {openReport.blockers && (
              <p className="daily-report__summary-blocker">
                <Icon name="warning" size={13} /> Blocked: {openReport.blockers}
              </p>
            )}
            {openReport.tomorrowsPlan && (
              <p className="daily-report__history-label">
                Next: <span>{openReport.tomorrowsPlan}</span>
              </p>
            )}
          </div>
        )}
      </Modal>
    </PageBody>
  );
}
